/* eslint-disable @typescript-eslint/no-unsafe-return */
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, catchError, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";
import { QPilotAccessToken } from "../../../core/services/qpilot.service";
import {
  SurveyListResponse,
  SurveyByIdResponse,
  CreateSurveyRequest,
  UpdateSurveyRequest,
  SurveyActionResponse,
  SurveyFilterParams,
  PredefinedAnswer,
} from "../interfaces/retention-workflow.interface";

@Injectable({
  providedIn: "root",
})
export class RetentionWorkflowService {
  private readonly baseUrl = environment.qpilotNonCoreApiUrl;

  constructor(private readonly http: HttpClient) {}

  /**
   * Creates HTTP headers with authorization token
   * Matches the pattern used in qpilot.service.ts
   */
  private createHeaders(accessToken: QPilotAccessToken): HttpHeaders {
    let headers = new HttpHeaders();

    if (accessToken) {
      if (accessToken.TokenBearerAuth) {
        headers = headers.set(
          "Authorization",
          `Bearer ${accessToken.TokenBearerAuth}`,
        );
      } else if (accessToken.TokenAuth) {
        headers = headers.set(
          "Authorization",
          `Basic ${accessToken.TokenAuth}`,
        );
      }
    }

    return headers;
  }

  /**
   * Get all surveys for a site with optional filters
   */
  getSurveys(
    siteId: number,
    accessToken: QPilotAccessToken,
    filters?: SurveyFilterParams,
  ): Observable<SurveyListResponse> {
    const endpoint = `${this.baseUrl}/v1/survey/${siteId}`;
    let params = new HttpParams();

    if (filters) {
      if (filters.Enabled !== undefined) {
        params = params.append("Enabled", filters.Enabled.toString());
      }
      if (filters.Deleted !== undefined) {
        params = params.append("Deleted", filters.Deleted.toString());
      }
      if (filters.ActionTrigger && filters.ActionTrigger.length > 0) {
        for (const trigger of filters.ActionTrigger) {
          params = params.append("ActionTrigger", trigger.toString());
        }
      }
    }

    return this.http
      .get<SurveyListResponse>(endpoint, {
        params,
        headers: this.createHeaders(accessToken),
      })
      .pipe(
        catchError((error) => {
          console.error("Error fetching surveys:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Get a single survey by ID
   */
  getSurveyById(
    siteId: number,
    surveyId: string,
    accessToken: QPilotAccessToken,
  ): Observable<SurveyByIdResponse> {
    const endpoint = `${this.baseUrl}/v1/survey/${siteId}/${surveyId}`;
    return this.http
      .get<SurveyByIdResponse>(endpoint, {
        headers: this.createHeaders(accessToken),
      })
      .pipe(
        catchError((error) => {
          console.error("Error fetching survey by ID:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Get predefined answers for autocomplete
   */
  getPredefinedAnswers(
    siteId: number,
    accessToken: QPilotAccessToken,
    trigger?: string,
  ): Observable<PredefinedAnswer[]> {
    const endpoint = `${this.baseUrl}/v1/survey/${siteId}/answers`;
    let params = new HttpParams();
    if (trigger) {
      params = params.append("trigger", trigger);
    }
    return this.http
      .get<PredefinedAnswer[]>(endpoint, {
        params,
        headers: this.createHeaders(accessToken),
      })
      .pipe(
        catchError((error) => {
          console.error("Error fetching predefined answers:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Create a new survey
   */
  createSurvey(
    request: CreateSurveyRequest,
    accessToken: QPilotAccessToken,
  ): Observable<SurveyActionResponse> {
    const endpoint = `${this.baseUrl}/v1/survey/${request.siteId}`;
    return this.http
      .post<SurveyActionResponse>(endpoint, request, {
        headers: this.createHeaders(accessToken),
      })
      .pipe(
        catchError((error) => {
          console.error("Error creating survey:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Update an existing survey
   */
  updateSurvey(
    request: UpdateSurveyRequest,
    accessToken: QPilotAccessToken,
  ): Observable<SurveyActionResponse> {
    const endpoint = `${this.baseUrl}/v1/survey/${request.siteId}`;
    return this.http
      .put<SurveyActionResponse>(endpoint, request, {
        headers: this.createHeaders(accessToken),
      })
      .pipe(
        catchError((error) => {
          console.error("Error updating survey:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Delete a survey
   */
  deleteSurvey(
    siteId: number,
    surveyId: string,
    accessToken: QPilotAccessToken,
  ): Observable<void> {
    const endpoint = `${this.baseUrl}/v1/survey/${siteId}/${surveyId}`;
    return this.http
      .delete<void>(endpoint, {
        headers: this.createHeaders(accessToken),
      })
      .pipe(
        catchError((error) => {
          console.error("Error deleting survey:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Enable a survey
   */
  enableSurvey(
    siteId: number,
    surveyId: string,
    accessToken: QPilotAccessToken,
  ): Observable<SurveyActionResponse> {
    const endpoint = `${this.baseUrl}/v1/survey/${siteId}/${surveyId}/enable`;
    return this.http
      .post<SurveyActionResponse>(
        endpoint,
        {},
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(
        catchError((error) => {
          console.error("Error enabling survey:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Disable a survey
   */
  disableSurvey(
    siteId: number,
    surveyId: string,
    accessToken: QPilotAccessToken,
  ): Observable<SurveyActionResponse> {
    const endpoint = `${this.baseUrl}/v1/survey/${siteId}/${surveyId}/disable`;
    return this.http
      .post<SurveyActionResponse>(
        endpoint,
        {},
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(
        catchError((error) => {
          console.error("Error disabling survey:", error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Duplicate a survey
   */
  duplicateSurvey(
    siteId: number,
    surveyId: string,
    accessToken: QPilotAccessToken,
  ): Observable<SurveyActionResponse> {
    const endpoint = `${this.baseUrl}/v1/survey/${siteId}/${surveyId}/duplicate`;
    return this.http
      .post<SurveyActionResponse>(
        endpoint,
        {},
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(
        catchError((error) => {
          console.error("Error duplicating survey:", error);
          return throwError(() => error);
        }),
      );
  }
}
